import { Injectable } from '@angular/core';
import { Firestore, doc, docData, collection, query, where, getDocs, setDoc, updateDoc, deleteDoc } from '@angular/fire/firestore';
import { Observable, of } from 'rxjs';
import { Logger } from '../logs/logger.service';

export interface UserData {
  id: string;
  email: string | null;
  first_name?: string | null;
  last_name?: string | null;
  display?: string | null;
  role?: string | null;
  appRoles?: string[];
  accountType?: string | null;
  inspector?: boolean;
  imageUrl?: string | null;
  signatureUrl?: string | null;
  active?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

@Injectable({
  providedIn: 'root'
})
export class UserDataProvider {
  constructor(
    private firestore: Firestore,
    private logger: Logger
  ) {}

  /**
   * Get user data from Firestore by user ID
   * @param uid User ID
   * @returns Observable of user data or null
   */
  getUserData(uid: string): Observable<UserData | null> {
    try {
      const userDocRef = doc(this.firestore, `users/${uid}`);
      return docData(userDocRef, { idField: 'id' }) as Observable<UserData | null>;
    } catch (error: any) {
      this.logger.error(`Get user data failed: ${error.message}`);
      return of(null);
    }
  }

  /**
   * Create user data in Firestore (for new user registration)
   * @param uid User ID
   * @param data User data to create
   * @returns Promise that resolves when creation is complete
   */
  async createUserData(uid: string, data: Partial<UserData>): Promise<void> {
    try {
      const defaults: Partial<UserData> = {
        first_name: '',
        last_name: '',
        display: '',
        email: null,
        role: 'CLIENT',
        accountType: 'client',
        appRoles: ['CLIENT'],
        inspector: false,
        active: true,
        imageUrl: null,
        signatureUrl: null,
      };
      const now = new Date();
      const userDocRef = doc(this.firestore, `users/${uid}`);
      await setDoc(userDocRef, { ...defaults, ...data, createdAt: now, updatedAt: now });
      this.logger.info(`User data created for ${uid}`);
    } catch (error: any) {
      this.logger.error(`Create user data failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Set last login time for a user (uses setDoc with merge so it works even if the document doesn't exist yet)
   * @param uid User ID
   */
  async setLastLogIn(uid: string): Promise<void> {
    const userDocRef = doc(this.firestore, `users/${uid}`);
    const date = new Date();
    await setDoc(userDocRef, { lastLogIn: date.toLocaleDateString(), updatedAt: date }, { merge: true });
  }

  /**
   * Update user data in Firestore
   * @param uid User ID
   * @param data Partial user data to update
   * @returns Promise that resolves when update is complete
   */
  async updateUserData(uid: string, data: Partial<UserData>): Promise<void> {
    try {
      const userDocRef = doc(this.firestore, `users/${uid}`);
      await updateDoc(userDocRef, { ...data, updatedAt: new Date() });
      this.logger.info(`User data updated for ${uid}`);
    } catch (error: any) {
      this.logger.error(`Update user data failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Search for user by email
   * @param email User email to search for
   * @returns Promise that resolves with user data or null
   */
  async getUserByEmail(email: string): Promise<UserData | null> {
    try {
      const usersCollection = collection(this.firestore, 'users');
      const q = query(usersCollection, where('email', '==', email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const docSnap = querySnapshot.docs[0];
      this.logger.info(`Found user by email: ${email}`);
      return docSnap.data() as UserData;
    } catch (error: any) {
      this.logger.error(`Get user by email failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get all users from Firestore
   * @returns Promise that resolves with array of users
   */
  async getAllUsers(): Promise<UserData[]> {
    try {
      const usersCollection = collection(this.firestore, 'users');
      const querySnapshot = await getDocs(usersCollection);

      if (querySnapshot.empty) {
        return [];
      }

      const users = querySnapshot.docs.map(doc => doc.data() as UserData);
      this.logger.info(`Retrieved ${users.length} users`);
      return users;
    } catch (error: any) {
      this.logger.error(`Get all users failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Delete user data from Firestore
   * @param uid User ID
   * @returns Promise that resolves when deletion is complete
   */
  async deleteUserData(uid: string): Promise<void> {
    try {
      const userDocRef = doc(this.firestore, `users/${uid}`);
      await deleteDoc(userDocRef);
      this.logger.info(`User data deleted for ${uid}`);
    } catch (error: any) {
      this.logger.error(`Delete user data failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Subscribe to user data changes
   * @param uid User ID
   * @returns Observable that emits user data whenever it changes
   */
  subscribeToUser(uid: string): Observable<UserData | null> {
    return this.getUserData(uid);
  }
}
